import React, { useState } from 'react';
import './ChildSafety.css';

interface Section {
  id: string;
  title: string;
  content?: string;
  items?: string[];
  contact?: { label: string; value: string; href?: string }[];
}

const sections: Section[] = [
  {
    id: 'commitment',
    title: 'Our Commitment',
    content:
      'BasniConnect is a job posting, announcement, and marketplace platform — not a social media platform. We are committed to maintaining a safe environment for all users and have a zero-tolerance policy toward child sexual abuse and exploitation (CSAE). We actively work to prevent, detect, and respond to any content or behaviour that endangers children.',
  },
  {
    id: 'prohibited',
    title: 'Prohibited Content & Behaviour',
    items: [
      'Child sexual abuse material (CSAM) of any kind — images, video, text, or audio',
      'Grooming, solicitation, or exploitation of minors in any form',
      'Sharing, distributing, or requesting sexual content involving minors',
      'Any communication that sexualises or endangers children',
      'Attempting to establish inappropriate contact with minors',
    ],
  },
  {
    id: 'detection',
    title: 'Prevention & Detection',
    content:
      'We implement the following measures to prevent CSAE on our platform:',
    items: [
      'User reporting tools available on all content and profiles',
      'Moderation review of reported content and accounts',
      'Immediate suspension of accounts found violating child safety policies',
      'Cooperation with law enforcement and relevant authorities upon valid legal requests',
      'Regular review of our safety policies to align with industry best practices',
    ],
  },
  {
    id: 'reporting',
    title: 'How to Report',
    content:
      'If you encounter any content or behaviour on BasniConnect that you believe violates child safety standards, please report it immediately using the in-app report feature on the relevant post or profile, or contact us directly:',
    contact: [
      { label: 'Email', value: 'mohammedrazachandwala@gmail.com', href: 'mailto:mohammedrazachandwala@gmail.com' },
      { label: 'App', value: 'BasniConnect' },
      { label: 'Location', value: 'Mumbai, India' },
    ],
  },
  {
    id: 'law-enforcement',
    title: 'Law Enforcement',
    content:
      'BasniConnect cooperates fully with law enforcement agencies investigating child exploitation. Any confirmed CSAM discovered on our platform is reported to the National Center for Missing & Exploited Children (NCMEC) CyberTipline and relevant Indian authorities as required by law.',
  },
  {
    id: 'minors',
    title: 'Age Restrictions',
    content:
      'BasniConnect is a job posting, announcement, and marketplace platform intended for users aged 13 and above. It is not a social media platform. Users under 13 are not permitted to create accounts. If we become aware that a child under 13 has registered, we immediately terminate their account and remove associated data. Users between 13 and 17 may use the platform solely for viewing job listings, announcements, and marketplace content in a read-appropriate manner consistent with our community guidelines.',
  },
  {
    id: 'updates',
    title: 'Policy Updates',
    content:
      'We review and update these child safety standards regularly to ensure compliance with applicable laws and Google Play policies. Continued use of the app constitutes acceptance of the current version of these standards.',
  },
];

const ChildSafety: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('commitment');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="cs-root">
      <header className="cs-header">
        <div className="cs-header-inner">
          <div className="cs-logo">
            <img src="/app_logo.png" alt="BasniConnect" className="cs-logo-img" />
            <span className="cs-logo-text">BasniConnect</span>
          </div>
          <div className="cs-header-meta">
            <span className="cs-badge">Child Safety Standards</span>
            <span className="cs-updated">Last updated: May 3, 2026</span>
          </div>
        </div>
      </header>

      <div className="cs-body">
        <nav className="cs-sidebar">
          <p className="cs-sidebar-label">Contents</p>
          <ul className="cs-nav-list">
            {sections.map((s, i) => (
              <li key={s.id}>
                <button
                  className={`cs-nav-item ${activeSection === s.id ? 'active' : ''}`}
                  onClick={() => scrollTo(s.id)}
                >
                  <span className="cs-nav-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="cs-nav-title">{s.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="cs-content">
          <div className="cs-hero">
            <div className="cs-hero-top">
              <span className="cs-hero-badge">CSAE Policy</span>
            </div>
            <h1 className="cs-title">Child Safety Standards</h1>
            <p className="cs-subtitle">
              BasniConnect is a job posting, announcement, and marketplace platform for
              users aged 13 and above. We maintain a strict zero-tolerance policy against
              child sexual abuse and exploitation. This page outlines our standards,
              prevention measures, and reporting mechanisms.
            </p>
          </div>

          <div className="cs-alert">
            <span className="cs-alert-icon">⚠</span>
            <p className="cs-alert-text">
              If you have found content that exploits a child, please report it immediately
              via the in-app report button or email us at{' '}
              <a href="mailto:mohammedrazachandwala@gmail.com" className="cs-alert-link">
                mohammedrazachandwala@gmail.com
              </a>
              . We act on every report.
            </p>
          </div>

          {sections.map((section, i) => (
            <section key={section.id} id={section.id} className="cs-section">
              <div className="cs-section-header">
                <span className="cs-section-num">{String(i + 1).padStart(2, '0')}</span>
                <h2 className="cs-section-title">{section.title}</h2>
              </div>

              {section.content && <p className="cs-text">{section.content}</p>}

              {section.items && (
                <ul className="cs-list">
                  {section.items.map((item) => (
                    <li key={item} className="cs-list-item">
                      <span className="cs-list-dot" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {section.contact && (
                <div className="cs-contact-card">
                  {section.contact.map((row) => (
                    <div key={row.label} className="cs-contact-row">
                      <span className="cs-contact-label">{row.label}</span>
                      {row.href ? (
                        <a href={row.href} className="cs-contact-link">{row.value}</a>
                      ) : (
                        <span className="cs-contact-value">{row.value}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          <footer className="cs-footer">
            <div className="cs-footer-logo">
              <img src="/app_logo.png" alt="BasniConnect" className="cs-logo-img cs-logo-img--sm" />
              <span className="cs-logo-text">BasniConnect</span>
            </div>
            <p className="cs-footer-text">
              © {new Date().getFullYear()} BasniConnect. All rights reserved.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default ChildSafety;
